import type { FormattedReaction } from '@adamdotai/design-system/src/components/InlineReactions/types'
import type {
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import { DecoratorNode } from 'lexical'
import { type JSX } from 'react'
import type { InlineIndicatorsChangeEvent } from '../../types/content-items'
import type { AdamContentItemNode } from '../AdamLexicalNode'

type InlineIndicatorsSupportedItemType =
    | 'agenda'
    | 'action'
    | 'decision'
    | 'note'

type InlineIndicatorsPayload = {
    itemId: string
    itemType: InlineIndicatorsSupportedItemType
    linksCount: number
    commentsCount: number
    tagsCount: number
    reactions: Array<FormattedReaction>
}

const DEFAULT_INLINE_INDICATORS_PAYLOAD: InlineIndicatorsPayload = {
    itemId: '',
    itemType: 'action',
    linksCount: 0,
    commentsCount: 0,
    tagsCount: 0,
    reactions: [],
}

type SerializedInlineIndicatorsNode = Spread<
    {
        type: 'inline-indicators'
        payload: InlineIndicatorsPayload
        version: 1
    },
    SerializedLexicalNode
>

class InlineIndicatorsNodeHeadless extends DecoratorNode<JSX.Element> {
    __payload: InlineIndicatorsPayload

    constructor(paylaod?: Partial<InlineIndicatorsPayload>, key?: NodeKey) {
        super(key)
        this.__payload = {
            ...DEFAULT_INLINE_INDICATORS_PAYLOAD,
            ...paylaod,
        }
    }

    static getType(): 'inline-indicators' {
        return 'inline-indicators'
    }

    static clone(
        node: InlineIndicatorsNodeHeadless,
    ): InlineIndicatorsNodeHeadless {
        return $createInlineIndicatorsNodeHeadless(node.__payload, node.__key)
    }

    static importJSON(
        serializedNode: SerializedInlineIndicatorsNode,
    ): InlineIndicatorsNodeHeadless {
        return $createInlineIndicatorsNodeHeadless(serializedNode.payload)
    }

    exportJSON(): SerializedInlineIndicatorsNode {
        return {
            payload: this.__payload,
            type: 'inline-indicators',
            version: 1,
        }
    }

    getTextContent(): string {
        return ''
    }

    updatePayload(payload: Partial<InlineIndicatorsPayload>): void {
        const writable = this.getWritable()
        writable.__payload = {
            ...this.__payload,
            ...payload,
        }
    }

    getId(): string {
        return this.__payload.itemId
    }

    setId(id: string) {
        this.updatePayload({ itemId: id })
    }

    getReactions() {
        return this.__payload.reactions
    }

    setReactions(reactions: Array<FormattedReaction>) {
        this.updatePayload({ reactions })
    }

    onChange(event: InlineIndicatorsChangeEvent) {
        this.updatePayload(event)

        const container =
            this.getTopLevelElement() as unknown as AdamContentItemNode

        container.onChildChange?.(this.getType(), event)
    }
}

function $createInlineIndicatorsNodeHeadless(
    payload?: Partial<InlineIndicatorsPayload>,
    nodeKey?: NodeKey,
): InlineIndicatorsNodeHeadless {
    return new InlineIndicatorsNodeHeadless(payload, nodeKey)
}

function $isInlineIndicatorsNodeHeadless(
    node: LexicalNode | null | undefined,
): node is InlineIndicatorsNodeHeadless {
    return node instanceof InlineIndicatorsNodeHeadless
}

export {
    type InlineIndicatorsPayload,
    type SerializedInlineIndicatorsNode,
    $isInlineIndicatorsNodeHeadless,
    $createInlineIndicatorsNodeHeadless,
    InlineIndicatorsNodeHeadless,
}
